import React, { createContext, useContext, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

export const SUPPORTED_LOCALES = [
  "en-US", "en-AU", "en-UK", "en-CA", "en-NZ", "de-AT", "fr-BE", "bg-BG", "hr-HR", "cs-CZ", "da-DK",
  "nl-NL", "et-EE", "fi-FI", "fr-FR", "de-DE", "el-GR", "hu-HU", "en-IE",
  "it-IT", "lv-LV", "lt-LT", "fr-LU", "mt-MT", "pl-PL", "pt-PT", "ro-RO",
  "sk-SK", "sl-SI", "es-ES", "sv-SE", "el-CY",
] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: SupportedLocale = "en-US";

const LOCALE_COUNTRY_MAP: Record<SupportedLocale, string> = {
  "en-US": "US", "en-AU": "AU", "en-UK": "GB", "en-CA": "CA", "en-NZ": "NZ", "de-AT": "AT", "fr-BE": "BE", "bg-BG": "BG",
  "hr-HR": "HR", "cs-CZ": "CZ", "da-DK": "DK", "nl-NL": "NL", "et-EE": "EE",
  "fi-FI": "FI", "fr-FR": "FR", "de-DE": "DE", "el-GR": "GR", "hu-HU": "HU",
  "en-IE": "IE", "it-IT": "IT", "lv-LV": "LV", "lt-LT": "LT", "fr-LU": "LU",
  "mt-MT": "MT", "pl-PL": "PL", "pt-PT": "PT", "ro-RO": "RO", "sk-SK": "SK",
  "sl-SI": "SI", "es-ES": "ES", "sv-SE": "SE", "el-CY": "CY",
};

/** Human-readable label for the locale picker */
export const LOCALE_LABELS: Record<SupportedLocale, string> = {
  "en-US": "US", "en-AU": "AU", "en-UK": "UK", "en-CA": "CA", "en-NZ": "NZ", "de-AT": "AT", "fr-BE": "BE", "bg-BG": "BG",
  "hr-HR": "HR", "cs-CZ": "CZ", "da-DK": "DK", "nl-NL": "NL", "et-EE": "EE",
  "fi-FI": "FI", "fr-FR": "FR", "de-DE": "DE", "el-GR": "GR", "hu-HU": "HU",
  "en-IE": "IE", "it-IT": "IT", "lv-LV": "LV", "lt-LT": "LT", "fr-LU": "LU",
  "mt-MT": "MT", "pl-PL": "PL", "pt-PT": "PT", "ro-RO": "RO", "sk-SK": "SK",
  "sl-SI": "SI", "es-ES": "ES", "sv-SE": "SE", "el-CY": "CY",
};

/** Map country code (from IP API) → locale */
const COUNTRY_TO_LOCALE: Record<string, SupportedLocale> = {
  US: "en-US", AU: "en-AU", GB: "en-UK", CA: "en-CA", NZ: "en-NZ", AT: "de-AT", BE: "fr-BE", BG: "bg-BG",
  HR: "hr-HR", CZ: "cs-CZ", DK: "da-DK", NL: "nl-NL", EE: "et-EE",
  FI: "fi-FI", FR: "fr-FR", DE: "de-DE", GR: "el-GR", HU: "hu-HU",
  IE: "en-IE", IT: "it-IT", LV: "lv-LV", LT: "lt-LT", LU: "fr-LU",
  MT: "mt-MT", PL: "pl-PL", PT: "pt-PT", RO: "ro-RO", SK: "sk-SK",
  SI: "sl-SI", ES: "es-ES", SE: "sv-SE", CY: "el-CY",
};

export function getCountryForLocale(locale: SupportedLocale): string {
  return LOCALE_COUNTRY_MAP[locale] || "US";
}

export function isValidLocale(value: string): value is SupportedLocale {
  return SUPPORTED_LOCALES.includes(value as SupportedLocale);
}

interface LocaleContextValue {
  locale: SupportedLocale;
  country: string;
  localePath: (path: string) => string;
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: DEFAULT_LOCALE,
  country: "US",
  localePath: (path) => `/${DEFAULT_LOCALE}${path}`,
});

export const useLocale = () => useContext(LocaleContext);

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { locale: localeParam } = useParams<{ locale: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locale: SupportedLocale = localeParam && isValidLocale(localeParam) ? localeParam : DEFAULT_LOCALE;
  const country = getCountryForLocale(locale);

  useEffect(() => {
    if (getUserSelectedLocale()) return;

    detectUserLocale().then((detectedLocale) => {
      if (detectedLocale === locale) return;

      const pathWithoutLocale = location.pathname.replace(`/${locale}`, "") || "";
      navigate(`/${detectedLocale}${pathWithoutLocale}${location.search}${location.hash}`, { replace: true });
    });
  }, [locale, location.hash, location.pathname, location.search, navigate]);

  const localePath = (path: string) => {
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `/${locale}${cleanPath}`;
  };

  return (
    <LocaleContext.Provider value={{ locale, country, localePath }}>
      {children}
    </LocaleContext.Provider>
  );
};

// Storage key for detected locale
const LOCALE_STORAGE_KEY = "detected-locale-v3";
const USER_SELECTED_LOCALE_STORAGE_KEY = "user-selected-locale";

export function getUserSelectedLocale(): SupportedLocale | null {
  const stored = localStorage.getItem(USER_SELECTED_LOCALE_STORAGE_KEY);
  return stored && isValidLocale(stored) ? stored : null;
}

export function setUserSelectedLocale(locale: SupportedLocale) {
  localStorage.setItem(USER_SELECTED_LOCALE_STORAGE_KEY, locale);
  sessionStorage.setItem(LOCALE_STORAGE_KEY, locale);
}

function localeFromBrowser(): SupportedLocale | null {
  const languages = navigator.languages?.length
    ? navigator.languages
    : [navigator.language || (navigator as any).userLanguage || ""];

  for (const language of languages) {
    const normalized = language.replace("_", "-");
    if (isValidLocale(normalized)) return normalized;

    const countryCode = normalized.split("-")[1]?.toUpperCase();
    if (countryCode && COUNTRY_TO_LOCALE[countryCode]) {
      return COUNTRY_TO_LOCALE[countryCode];
    }
  }

  return null;
}

async function countryFromCloudflare(): Promise<string | null> {
  try {
    const res = await fetch("/cdn-cgi/trace", { signal: AbortSignal.timeout(2500), cache: "no-store" });
    if (!res.ok) return null;
    const text = await res.text();
    const loc = text.match(/^loc=([A-Z]{2})$/m)?.[1];
    return loc || null;
  } catch {
    return null;
  }
}

async function countryFromIpApi(): Promise<string | null> {
  try {
    const response = await fetch("https://ipapi.co/json/", { signal: AbortSignal.timeout(3000) });
    if (!response.ok) return null;
    const data = await response.json();
    return typeof data?.country_code === "string" ? data.country_code : null;
  } catch {
    return null;
  }
}

async function detectFreshUserLocale(): Promise<SupportedLocale> {
  // 1. Cloudflare edge geo (same-origin, fast, no rate limits)
  const cf = await countryFromCloudflare();
  if (cf && COUNTRY_TO_LOCALE[cf]) return COUNTRY_TO_LOCALE[cf];

  // 2. Browser locale (reflects the user's own region settings)
  const fromBrowser = localeFromBrowser();
  if (fromBrowser) return fromBrowser;

  // 3. IP lookup as a last resort
  const ip = await countryFromIpApi();
  if (ip && COUNTRY_TO_LOCALE[ip]) return COUNTRY_TO_LOCALE[ip];

  return DEFAULT_LOCALE;
}

export async function detectUserLocale({ force = false }: { force?: boolean } = {}): Promise<SupportedLocale> {
  const userSelected = getUserSelectedLocale();
  if (userSelected) return userSelected;

  // Check if we already detected and cached
  const cached = force ? null : sessionStorage.getItem(LOCALE_STORAGE_KEY);
  if (cached && isValidLocale(cached)) return cached;

  const detected = await detectFreshUserLocale();

  sessionStorage.setItem(LOCALE_STORAGE_KEY, detected);
  return detected;
}

/** Get flag icon CSS class from country code */
export function countryCodeToFlag(countryCode: string): string {
  return `fi fi-${countryCode.toLowerCase()}`;
}
